import React from "react";
import { FileSpreadsheet, Filter, Rocket, Zap, Wrench, Pin, Target } from "lucide-react"; 
export default function WhatIsTool() {
  const manualFlow = [
    "Switching between Moz, Ahrefs, and LinkedIn for every domain.",
    "Copy-pasting DA, PA, and Spam Score manually.",
    "Managing 10+ Google tabs with endless “site:” and “intitle:” queries.",
    "Forgetting which websites you’ve already contacted.",
    "Maintaining messy spreadsheets and outdated data.",
  ];

  const automatedFlow = [
{
  icon: Filter,
  title: "Automated Discovery",
  desc: "Quickly uncover relevant, high-authority websites for backlinks using advanced search operators and intent-based filters - no manual prospecting required.",
},
{
  icon: Rocket,
  title: "Instant SEO Metrics",
  desc: "Evaluate each site's DA,PA and Spam Score instantly.",
},
{
  icon: Zap,
  title: "Built-In Outreach Intelligence",
  desc: "Access verified contact data- connect directly with decision-makers faster.",
},
{
  icon: Wrench,
  title: "Smart Relevance Filtering",
  desc: "Automatically remove irrelevant, low-quality, or spammy sites. Focus only on credible domains that can actually move rankings.",
},
{
  icon: Target,
  title: "Category & Keyword Targeting",
  desc: "Refine your searches by niche, content theme, or keyword intent to uncover websites that align perfectly with your backlink goals.",
},
{
  icon: FileSpreadsheet,
  title: "Easy Export & Collaboration",
  desc: "Export qualified prospects to CSV or Sheets and share them with your team.",
},

  ];

  const bananaAdvantages = [
    {
      Icon: Rocket,
      title: "Quality Over Quantity",
      description:
        "Find relevant, high-authority websites for backlinks using smart Google operator searches - no manual research required.",
    },
    {
      Icon: Wrench,
      title: "Outreach, Not Data Entry",
      description:
        "Each result includes DA, PA, and Spam Score instantly - no need for extensions or manual checks - no more spreadsheets or browser hopping.",
    },
    {
      Icon: Pin,
      title: "Prospecting, Simplified",
      description:
        "Built for SEOs and agencies, Backlink Banana turns complex link research into a two-click process. No exports, no confusion, just clarity.",
    },
  ];

  return (
    <section className="relative py-24 bg-white">
      <div className="max-w-6xl mx-auto px-6 text-center">
        {/* ----- Section Header ----- */}
        <h2 className="text-4xl sm:text-5xl font-extrabold text-blue-900 mb-8">
          What Is a{" "}
          <span className="bg-gradient-to-r from-blue-600 to-green-500 bg-clip-text text-transparent">
            Link Prospecting Tool?
          </span>
        </h2>

        <p className="text-gray-700 text-lg sm:text-xl leading-relaxed max-w-3xl mx-auto mb-20">
          A link prospecting tool automates the process of finding potential websites for link building -
          helping SEOs and marketers identify high-value opportunities faster and streamline outreach.
        </p>

        {/* ----- How It Works Section ----- */}
        <h3 className="text-3xl font-bold text-blue-900 mb-6">
          How a{" "}
          <span className="bg-gradient-to-r from-blue-600 to-teal-500 bg-clip-text text-transparent">
            Link Prospecting Tool
          </span>{" "}
          Works
        </h3>

<div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 mb-24">
  {/* Left: Manual workflow */}
  <div className="bg-gray-50 border border-gray-100 rounded-2xl shadow-sm p-10">
    <div className="flex items-center gap-3 mb-8">
      <FileSpreadsheet className="h-8 w-8 text-red-500" />
      <h4 className="text-2xl font-semibold text-gray-800">
        With Sheets & 3rd-Party Tools
      </h4>
    </div>
    <ul className="space-y-3 text-gray-700 text-[17px] leading-relaxed pl-1">
      {manualFlow.map((p, i) => (
        <li
          key={i}
          className="flex items-start gap-3 text-left"
        >
          <span className="text-blue-500 text-lg leading-[1.4] mt-1">•</span>
          <span className="flex-1">{p}</span>
        </li>
      ))}
    </ul>
  </div>

  {/* Right: Automated workflow */}
  <div className="bg-white border border-green-100 rounded-2xl shadow-sm p-10">
    <div className="flex items-center gap-3 mb-8">
      <Rocket className="h-8 w-8 text-green-600" />
      <h4 className="text-2xl font-semibold text-green-700">
        With a Link Prospecting Tool
      </h4>
    </div>

    <div className="space-y-6">
      {automatedFlow.map(({ icon: Icon, title, desc }, i) => (
        <div key={i} className="flex items-start gap-4 text-left">
          <Icon className="h-5 w-5 text-green-600 mt-1 shrink-0" />
          <div className="flex-1">
            <h5 className="text-[18px] font-semibold text-gray-900 mb-1 leading-tight text-left">
              {title}
            </h5>
            <p className="text-[16px] text-gray-700 leading-relaxed text-left">
              {desc}
            </p>
          </div>
        </div>
      ))}
    </div>
  </div>
</div>


        {/* ----- Why Backlink Banana Section ----- */}
        <h3 className="text-3xl font-bold text-blue-900 mb-8">
          Why{" "}
          <span className="bg-gradient-to-r from-yellow-500 to-orange-600 bg-clip-text text-transparent">
            Backlink Banana
          </span>{" "}
          Beats the Rest
        </h3>

        <p className="text-lg text-gray-700 max-w-2xl mx-auto mb-12">
          Many link prospecting tools focus on volume or complexity.
          Backlink Banana keeps things simple, fast, and accurate - built for link builders who value precision over noise.
        </p>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-10">
          {bananaAdvantages.map(({ Icon, title, description }, i) => (
            <div
              key={i}
              className="bg-gray-50 border border-gray-100 rounded-2xl shadow-sm hover:shadow-md p-8 text-left transition-all duration-300"
            >
              <div className="flex items-center mb-4">
                <Icon className="h-8 w-8 text-blue-600 mr-3" />
                <h4 className="text-xl font-semibold text-gray-900">{title}</h4>
              </div>
              <p className="text-gray-700 text-md leading-relaxed">{description}</p>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}
